import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { IntlProvider } from "react-intl";

import { LocaleProvider } from "@solarkey/ui";

/**
 * FormatJS (react-intl) root. Numbers, dates and currency are formatted here from API fields
 * (UX-003: never a currency/number literal in code). Locale starts at a default for the login
 * screen, then SessionGate calls setLocale(capabilities.locale) once the session bootstraps.
 *
 * Message catalogs land per-locale under ./messages; the empty dev catalog falls back to each
 * component's defaultMessage.
 */
const MESSAGES: Record<string, Record<string, string>> = { en: {} };

const SetLocaleContext = createContext<(locale: string) => void>(() => {
  /* replaced by IntlRoot */
});

export function useSetLocale() {
  return useContext(SetLocaleContext);
}

function baseLocale(locale: string): string {
  return locale.split("-")[0] ?? "en";
}

export function IntlRoot({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState("en");
  const set = useCallback((next: string) => setLocale(next), []);
  const messages = useMemo(() => MESSAGES[baseLocale(locale)] ?? MESSAGES["en"], [locale]);

  return (
    <SetLocaleContext.Provider value={set}>
      <IntlProvider
        locale={locale}
        defaultLocale="en"
        messages={messages}
        onError={(err) => {
          if (err.code === "MISSING_TRANSLATION" || err.code === "MISSING_DATA") return;
          console.error(err);
        }}
      >
        {/* Keep @solarkey/ui's number/date formatting on the same locale as react-intl. */}
        <LocaleProvider locale={locale}>{children}</LocaleProvider>
      </IntlProvider>
    </SetLocaleContext.Provider>
  );
}
